import { useMemo } from "react";

function makeStarShadows(count: number, spread: number): string {
  const parts: string[] = [];
  for (let i = 0; i < count; i++) {
    const x = Math.floor(Math.random() * spread);
    const y = Math.floor(Math.random() * spread);
    const alpha = (Math.random() * 0.6 + 0.4).toFixed(2);
    parts.push(`${x}px ${y}px rgba(255,255,255,${alpha})`);
  }
  return parts.join(", ");
}

/**
 * Hidden <defs> block shared by every UFO instance via url(#…) references.
 * Defining once avoids duplicating gradients on every craft.
 */
export function UfoDefs() {
  return (
    <svg
      className="cosmos-ufo-defs"
      aria-hidden="true"
      width="0"
      height="0"
      focusable="false"
    >
      <defs>
        <radialGradient id="cosmos-ufo-dome" cx="50%" cy="60%" r="50%">
          <stop offset="0%" stopColor="rgba(255,255,255,0.95)" />
          <stop offset="55%" stopColor="rgba(176,231,255,0.7)" />
          <stop offset="100%" stopColor="rgba(30,187,240,0)" />
        </radialGradient>
        <linearGradient id="cosmos-ufo-body" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#cad4ec" />
          <stop offset="55%" stopColor="#6f7aa4" />
          <stop offset="100%" stopColor="#2a2160" />
        </linearGradient>
        <linearGradient id="cosmos-ufo-beam" x1="0.5" y1="0" x2="0.5" y2="1">
          <stop offset="0%" stopColor="rgba(176,231,255,0.55)" />
          <stop offset="100%" stopColor="rgba(176,231,255,0)" />
        </linearGradient>
      </defs>
    </svg>
  );
}

export function UfoCraft() {
  return (
    <svg
      className="cosmos-ufo-svg"
      viewBox="0 0 120 100"
      aria-hidden="true"
      focusable="false"
    >
      <path
        className="cosmos-ufo-beam"
        d="M48 40 L72 40 L92 96 L28 96 Z"
        fill="url(#cosmos-ufo-beam)"
      />
      <ellipse cx="60" cy="38" rx="50" ry="9" fill="url(#cosmos-ufo-body)" />
      <ellipse cx="60" cy="40.5" rx="44" ry="2.6" fill="rgba(30,187,240,0.45)" />
      <ellipse cx="60" cy="31" rx="22" ry="14" fill="url(#cosmos-ufo-dome)" />
      <circle cx="22" cy="40" r="2" fill="#1ebbf0" />
      <circle cx="40" cy="42" r="2" fill="#7c3aed" />
      <circle cx="60" cy="43" r="2" fill="#f45c0f" />
      <circle cx="80" cy="42" r="2" fill="#1ebbf0" />
      <circle cx="98" cy="40" r="2" fill="#7c3aed" />
    </svg>
  );
}

export function CosmosBackdrop() {
  const starLayers = useMemo(
    () => ({
      small: makeStarShadows(220, 2000),
      medium: makeStarShadows(90, 2000),
      large: makeStarShadows(28, 2000),
    }),
    [],
  );

  return (
    <div className="cosmos-backdrop" aria-hidden="true">
      <div className="cosmos-nebula cosmos-nebula--cyan" />
      <div className="cosmos-nebula cosmos-nebula--violet" />
      <div className="cosmos-nebula cosmos-nebula--magenta" />

      <div className="cosmos-galaxy" />
      <div className="cosmos-galaxy cosmos-galaxy--inner" />

      <div
        className="cosmos-stars cosmos-stars--small"
        style={{ boxShadow: starLayers.small }}
      />
      <div
        className="cosmos-stars cosmos-stars--medium"
        style={{ boxShadow: starLayers.medium }}
      />
      <div
        className="cosmos-stars cosmos-stars--large"
        style={{ boxShadow: starLayers.large }}
      />

      <div className="cosmos-shooting cosmos-shooting--a" />
      <div className="cosmos-shooting cosmos-shooting--b" />

      {/* Easter egg: a rare UFO squadron drifts through every ~30–60s. */}
      <UfoDefs />
      <div className="cosmos-ufo cosmos-ufo--a">
        <div className="cosmos-ufo-craft">
          <UfoCraft />
        </div>
      </div>
      <div className="cosmos-ufo cosmos-ufo--b cosmos-ufo--mirror">
        <div className="cosmos-ufo-craft">
          <UfoCraft />
        </div>
      </div>

      <div className="cosmos-vignette" />

      <div className="cosmos-caption">
        <div className="cosmos-caption-eyebrow">AIHR · Cosmos</div>
        <div className="cosmos-caption-title">Where course intelligence meets curiosity.</div>
        <div className="cosmos-caption-sub">
          Your governed AIHR analytics copilot — ask about courses, usage, and journeys with cited sources.
        </div>
      </div>
    </div>
  );
}
